import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { FaClipboardCheck, FaRegTrashAlt, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/api";
import { IoIosArrowBack } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { FaPencil } from "react-icons/fa6";
import { useProfileStore } from "../../store/useUserStore";
import { driver } from "driver.js";
import { motion, AnimatePresence } from "framer-motion";
import { TbBuildingBank } from "react-icons/tb";
import { formatTimeSlotList } from "../../helpers/helper";
import { FaHistory } from "react-icons/fa";

interface Month {
  id: string;
  name: string;
  date: Date;
}

const PreEnroll = () => {
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    setHomePageShow,
    setSelectedStudent,
    setCartEdit,
    setLoading,
    selectedVouchers,
    setGuidanceStep,
    guidanceStep,
    showGuidanceFlow,
    setShowGuidanceFlow,
  } = useProfileStore();

  // Show driver.js popover for after-first-payment guidance
  useEffect(() => {
    if (guidanceStep === "after-first-payment") {
      const driverObj = driver({
        showProgress: false,
        showButtons: [],
        steps: [
          {
            element: "#payment-history-icon",
            popover: {
              description:
                "<img style='width: 170px; height: 166.65px;' src='/assets/img/5.png' />",
              side: "bottom",
              align: "center",
              onPopoverRender: (popover, { config }) => {
                const popoverElement = popover.wrapper;
                popoverElement.style.backgroundColor = "transparent";
                popoverElement.style.boxShadow = "none";
                const popoverDescription = popoverElement.querySelector(
                  "#driver-popover-description",
                ) as HTMLElement;
                if (popoverDescription) {
                  popoverDescription.style.textAlign = "center";
                }
              },
              showButtons: [],
            },
          },
        ],
        onCloseClick: () => {
          setGuidanceStep(null);
        },
        onPopoverRender: (popover, { config }) => {
          const popoverElement = popover.wrapper;
          const descriptionElement = popoverElement.querySelector(
            ".driver-popover-description",
          ) as HTMLElement;
          if (descriptionElement) {
            descriptionElement.style.textAlign = "center";
          }
        },
      });
      driverObj.drive();
    }
  }, [guidanceStep, setGuidanceStep]);

  // State to manage timeslots schedules modal
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState({
    course_name: "",
    branch_name: "",
    price: 0,
    schedules: [],
    classroom: "",
    pricing_method: "",
    selected_package: { count: 0 },
    selected_date: "",
    id: 0,
  });

  useEffect(() => {
    getPreEnrollList();
  }, []);

  function organizeCartData(data: any[]) {
    const organized: any[] = [];
    console.log("data", data);

    data.forEach((student) => {
      // Group cart items by branch
      const branchesMap: { [key: string]: any[] } = {};

      student.cart.forEach((item: any) => {
        const branchName = item.branch_name;

        if (!branchesMap[branchName]) {
          branchesMap[branchName] = [];
        }

        branchesMap[branchName].push(item);
      });

      // Convert branches map to array format
      const branches = Object.keys(branchesMap).map((branchName) => ({
        branch_name: branchName,
        items: branchesMap[branchName],
      }));

      // Add student with organized branches
      organized.push({
        student_id: student.student_id,
        name: student.student_name,
        branches: branches,
      });
    });

    console.log("organized cart data", organized);
    return organized;
  }

  // Usage in component:
  const [organizedCart, setOrganizedCart] = useState<any[]>([]);

  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const organized = organizeCartData(cartItems);
      setOrganizedCart(organized);
    }
  }, [cartItems]);

  function getPreEnrollList() {
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/preenroll/parent`)
      .then((res) => {
        console.log("res cart", res.data.data);
        setCartItems(res.data.data.students);
      })
      .catch((err) => {
        toast.error(t("server_error"));
        console.log("err cart", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function cancelPreEnrol(id: number) {
    setOpenScheduleModal(false);
    setLoading(true);
    api
      .delete(`${process.env.REACT_APP_API_BASE_URL}/preenroll/delete/${id}`)
      .then((res) => {
        toast.success(t("cart.pre_enrol_cancelled"));
        getPreEnrollList();
      })
      .catch((err) => {
        toast.error(t("server_error"));
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <div className="pb-20">
      <div className="mb-2 flex items-center justify-between pl-2 pr-3">
        <IoIosArrowBack
          size={28}
          color="#000"
          onClick={() => navigate("/cart")}
        />
        <div className="text-lg font-medium flex-1 text-center">
          {t("course_view.pre_enrol")}
        </div>
      </div>

      <div className="px-3">
        {cartItems && cartItems.length >= 1 ? (
          <>
            {organizedCart.map((student, studentIndex) => (
              <div key={studentIndex} className="border-gray-300 pb-8">
                <div className="text-lg font-semibold mt-3">{student.name}</div>
                {student.branches.map((branch: any, branchIndex: number) => (
                  <div key={branchIndex}>
                    <div className="flex items-center bg-blue-100 rounded-lg px-3 py-1 my-3">
                      <TbBuildingBank className="mr-2" />
                      <span className="font-medium text-sm text-gray-700">
                        {branch.branch_name}
                      </span>
                    </div>
                    {console.log("branch items", branch.items)}
                    {branch.items.map((item: any, index: number) => (
                      <div className="grid grid-cols-5 gap-3 mt-5" key={index}>
                        <div className="col-span-1 self-center relative">
                          {item.course.images &&
                          item.course.images.length > 0 ? (
                            <img
                              src={item.course.images[0]}
                              className="w-full aspect-square object-cover"
                            />
                          ) : (
                            <img
                              src={"/assets/img/products/product1.png"}
                              className="w-full aspect-square object-cover"
                            />
                          )}
                        </div>
                        <div className="col-span-4">
                          <div className="grid grid-cols-3 gap-1">
                            <div
                              className="col-span-2 cursor-pointer"
                              onClick={() => {
                                setSelectedCourse(item);
                                setOpenScheduleModal(true);
                              }}
                            >
                              <div className="text-lg font-medium truncate">
                                {item.course.name}
                              </div>
                              <div className="truncate">
                                {formatTimeSlotList(item.schedules)}
                              </div>
                            </div>
                            <div className="flex flex-col col-span-1">
                              <div className="self-end">
                                <div
                                  className={`rounded-xl text-center p-[0.12rem] px-2 text-xs truncate text-white bg-[#023f81]`}
                                >
                                  {t("course_view.pre_enrol")}
                                </div>
                              </div>
                              <div className="self-end mt-2">
                                <FaRegTrashAlt
                                  size={16}
                                  className="mt-1 cursor-pointer"
                                  onClick={() => cancelPreEnrol(item.id)}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-8 mt-2">
                            <div className=" whitespace-normal break-words col-span-5 flex justify-between items-center">
                              {item.pricing_method === "Month"
                                ? item.selected_date
                                : item.selected_package.count + " attd"}
                            </div>
                            <div className=" col-span-3 text-end font-medium">{`RM ${item.price}`}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center mt-52">
            <div className="flex items-center justify-center w-[100px] h-[70px] mb-5">
              <ReactSVG
                src={process.env.PUBLIC_URL + "/assets/img/icons/cart.svg"}
              />
            </div>
            <div>
              <p>
                {t("cart.no_items_in_pre_enroll")}{" "}
                <Link
                  to={process.env.PUBLIC_URL + "/"}
                  className="block underline font-medium"
                >
                  {t("cart.add_some")}
                </Link>
              </p>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {openScheduleModal && (
          <div
            className="fixed inset-0 z-10 bg-black bg-opacity-50 flex justify-center items-end"
            onClick={() => {
              setOpenScheduleModal(false);
            }}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ stiffness: 100 }}
              className="w-full max-w-[648px] bg-white shadow-2xl rounded-t-3xl px-4 pt-4 pb-20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid grid-cols-6 gap-1 pb-3 mb-3 border-b-2 border-gray-300">
                <div className="col-span-4">
                  <div className="text-lg font-medium">
                    {selectedCourse.course_name}
                  </div>
                  <div className="text-md mt-1 font-normal">
                    {selectedCourse.branch_name || "-"}
                  </div>
                </div>
                <div className="text-lg font-medium col-span-2 text-end">
                  RM {selectedCourse.price}
                </div>
              </div>

              <div className="mb-4 ">
                <div className="mb-1 text-base font-medium">
                  ✏️ {t("course_view.classroom")}{" "}
                </div>
                <p className="px-2 py-2">{selectedCourse.classroom}</p>
              </div>

              <div className="pt-2 border-gray-300 border-t-2 mb-3">
                <div className="mb-1 text-base font-medium">
                  📅 {t("course_view.class_day")}
                </div>
                {selectedCourse.schedules.map((slot: any, index) => {
                  const format12 = (time: string) => {
                    const [h, m] = time.split(":").map(Number);
                    const d = new Date();
                    d.setHours(h, m);
                    return d.toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                  };

                  return (
                    <div key={index} className="px-2 py-2">
                      {format12(slot?.start_time)} - {format12(slot?.end_time)}{" "}
                      {slot.day}
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 border-gray-300 border-t-2 mb-3">
                <div className="mb-1 text-base font-medium">
                  {t("course_view.payment_term")}
                </div>
                <div className="px-2 py-2">
                  {selectedCourse.selected_package.count}{" "}
                  {selectedCourse.pricing_method === "Month"
                    ? t("course_view.month")
                    : t("attendance.title")}
                </div>
              </div>

              {selectedCourse.pricing_method === "Month" && (
                <div className="pt-2 border-gray-300 border-t-2 mb-3">
                  <div className="mb-1 text-base font-medium">
                    {t("course_view.paying_for")}
                  </div>
                  <p className="px-2 py-2">{selectedCourse.selected_date}</p>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={() => cancelPreEnrol(selectedCourse.id)}
                  className="bg-[#ff3b30] w-3/4 flex gap-3 text-white justify-center text-center h-12 py-2 px-4 font-normal text-base items-center rounded-full cursor-pointer hover:bg-gray-400l mt-3"
                >
                  <FaClipboardCheck size={18} />
                  {t("cart.cancel_pre_enroll")}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PreEnroll;
